/* =============================================================
   SEARCH CONTEXT
   =============================================================
   The search a ship operator is in the middle of -- place,
   vessel, arrival, departure, amenities -- lives in the URL, not
   in a store. Search, compare, berth profile and book all read it
   from the query string, so every one of those screens can be
   linked to, reloaded, and stepped back through, and handing the
   search from one to the next is just passing the query along.

   `update()` replaces rather than pushes: changing a date on the
   search screen is refining one search, not a step to go back to.
   ============================================================= */

import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { searchQuery, type SearchContext } from '@/config/routes'
import { AMENITY_ORDER } from './amenities'
import type { AmenityCode } from './types'

function one(value: unknown): string | undefined {
  const v = Array.isArray(value) ? value[0] : value
  return typeof v === 'string' && v !== '' ? v : undefined
}

function isoOrUndefined(value: string | undefined): string | undefined {
  if (!value) return undefined
  return Number.isNaN(new Date(value).getTime()) ? undefined : value
}

export function useSearchContext() {
  const route = useRoute()
  const router = useRouter()

  const context = computed<SearchContext>(() => {
    const q = route.query
    const vessel = Number(one(q.vessel))
    const amenities = (one(q.amenities) ?? '')
      .split(',')
      .filter((a): a is AmenityCode => (AMENITY_ORDER as string[]).includes(a))
    return {
      city: one(q.city),
      vessel: Number.isInteger(vessel) && vessel > 0 ? vessel : undefined,
      from: isoOrUndefined(one(q.from)),
      to: isoOrUndefined(one(q.to)),
      amenities,
    }
  })

  /** Amenities as the typed codes the database takes. */
  const amenities = computed(() => (context.value.amenities ?? []) as AmenityCode[])

  /** Both dates present and in order -- only then are price and availability meaningful. */
  const hasStay = computed(() => {
    const { from, to } = context.value
    return Boolean(from && to && new Date(to).getTime() > new Date(from).getTime())
  })

  function update(patch: Partial<SearchContext>): void {
    const next = { ...context.value, ...patch }
    // Keep any query this helper does not own (e.g. compare's `ids`).
    const rest = { ...route.query }
    for (const key of ['city', 'vessel', 'from', 'to', 'amenities']) delete rest[key]
    void router.replace({ query: { ...rest, ...searchQuery(next) } })
  }

  return { context, amenities, hasStay, update }
}
